/** My Account area: wire types and funding constants shared by API and web. Decimals are strings. */
export const KYC_DOC_TYPES = [
    { id: 'passport', label: 'Passport', needsBack: false },
    { id: 'id_card', label: 'ID card', needsBack: true },
    { id: 'drivers_license', label: "Driver's license", needsBack: true },
];
/** Kinds a submission needs for the given document type. */
export function requiredKycKinds(docType) {
    const back = KYC_DOC_TYPES.find((d) => d.id === docType)?.needsBack;
    return back ? ['id_front', 'id_back', 'selfie', 'poa'] : ['id_front', 'selfie', 'poa'];
}
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
export const UPLOAD_MIME = ['image/jpeg', 'image/png', 'application/pdf'];
export const NETWORKS = [
    { id: 'TRC20', chain: 'TRON', confirmations: 20, eta: '~1 min', minDeposit: '10', minWithdrawal: '10' },
    { id: 'ERC20', chain: 'Ethereum', confirmations: 12, eta: '~3 min', minDeposit: '10', minWithdrawal: '20' },
    { id: 'BEP20', chain: 'BNB Chain', confirmations: 15, eta: '~1 min', minDeposit: '10', minWithdrawal: '10' },
];
export const networkInfo = (id) => NETWORKS.find((n) => n.id === id);
/** Address format check per network (TRON base58 "T…", EVM 0x + 40 hex). */
export function validAddress(network, address) {
    if (network === 'TRC20')
        return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
    return /^0x[0-9a-fA-F]{40}$/.test(address);
}
const BASE58 = '1-9A-HJ-NP-Za-km-z';
const TRON_RE = new RegExp(`^T[${BASE58}]{33}$`);
const EVM_RE = /^0x[0-9a-fA-F]{40}$/;
/** Legacy P2PKH "1…" / P2SH "3…", base58, 26–35 characters. */
const BTC_LEGACY_RE = new RegExp(`^[13][${BASE58}]{25,34}$`);
/** Native SegWit / Taproot "bc1…", bech32 alphabet (no 1, b, i, o after the prefix), lower case, 42–62 characters. */
const BTC_BECH32_RE = /^bc1[02-9ac-hj-np-z]{39,59}$/;
export const validBtcAddress = (a) => BTC_LEGACY_RE.test(a) || BTC_BECH32_RE.test(a);
export const WITHDRAWAL_ASSETS = [
    { id: 'USDT-TRC20', coin: 'USDT', network: 'TRC20', label: 'USDT · Tron (TRC20)', chain: 'Tron', dp: 2, fee: '0', min: '10', eta: '~1 min', addressHint: 'a Tron address starting with T (34 characters)', check: (a) => TRON_RE.test(a) },
    { id: 'USDT-ERC20', coin: 'USDT', network: 'ERC20', label: 'USDT · Ethereum (ERC20)', chain: 'Ethereum', dp: 2, fee: '0', min: '20', eta: '~3 min', addressHint: 'an Ethereum address, 0x and 40 hex characters', check: (a) => EVM_RE.test(a) },
    { id: 'BTC', coin: 'BTC', network: 'BTC', label: 'Bitcoin (BTC)', chain: 'Bitcoin', dp: 8, fee: '0', min: '50', eta: '~30 min', addressHint: 'a Bitcoin address starting with 1, 3 or bc1', check: validBtcAddress },
    { id: 'ETH', coin: 'ETH', network: 'ETH', label: 'Ether (ETH)', chain: 'Ethereum', dp: 8, fee: '0', min: '50', eta: '~3 min', addressHint: 'an Ethereum address, 0x and 40 hex characters', check: (a) => EVM_RE.test(a) },
];
export const WITHDRAWAL_ASSET_IDS = WITHDRAWAL_ASSETS.map((a) => a.id);
export const withdrawalAsset = (id) => WITHDRAWAL_ASSETS.find((a) => a.id === id);
/** The asset of a coin + network pair (a saved address or a withdrawal row); undefined when not offered (USDT on BEP20). */
export const withdrawalAssetOf = (coin, network) => WITHDRAWAL_ASSETS.find((a) => a.coin === (coin ?? 'USDT') && a.network === network);
/** Address check for a withdrawal asset; false for anything not offered. */
export const validWithdrawalAddress = (asset, address) => !!withdrawalAsset(asset)?.check(address);
// ---- profile & security ----
/** ISO 3166-1 alpha-2 codes. Display names come from Intl.DisplayNames in the browser. */
export const COUNTRY_CODES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
    'DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP ' +
    'KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ ' +
    'OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ ' +
    'UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
export const LANGUAGES = [
    { id: 'en', label: 'English' },
    { id: 'es', label: 'Español' },
    { id: 'fr', label: 'Français' },
    { id: 'de', label: 'Deutsch' },
    { id: 'pt', label: 'Português' },
    { id: 'it', label: 'Italiano' },
    { id: 'tr', label: 'Türkçe' },
    { id: 'ru', label: 'Русский' },
    { id: 'ar', label: 'العربية' },
    { id: 'zh', label: '中文' },
];
/** Phone country calling codes offered in the profile form. */
export const PHONE_CODES = ('+1 +7 +20 +27 +30 +31 +32 +33 +34 +36 +39 +40 +41 +43 +44 +45 +46 +47 +48 +49 +51 +52 +54 +55 +56 +57 +58 +60 +61 +62 +63 +64 +65 +66 +81 +82 +84 +86 +90 +91 +92 +93 +94 +95 +98 ' +
    '+211 +212 +213 +216 +218 +220 +221 +223 +225 +226 +227 +228 +229 +230 +231 +232 +233 +234 +235 +236 +237 +238 +239 +240 +241 +242 +243 +244 +248 +249 +250 +251 +252 +253 +254 +255 +256 +257 +258 +260 +261 +263 +264 +265 +266 +267 +268 +269 ' +
    '+350 +351 +352 +353 +354 +355 +356 +357 +358 +359 +370 +371 +372 +373 +374 +375 +376 +377 +378 +380 +381 +382 +383 +385 +386 +387 +389 +420 +421 +423 ' +
    '+501 +502 +503 +504 +505 +506 +507 +509 +591 +592 +593 +595 +597 +598 +852 +853 +855 +856 +880 +886 +960 +961 +962 +963 +964 +965 +966 +967 +968 +970 +971 +972 +973 +974 +975 +976 +977 +992 +993 +994 +995 +996 +998').split(' ');
export const WITHDRAWAL_REQUEST_STATUSES = ['pending', 'under_review', 'compliance', 'approved', 'paid', 'rejected', 'cancelled'];
/** Still open: the amount is held and the request can still change. */
export const OPEN_WITHDRAWAL_STATUSES = ['pending', 'under_review', 'compliance', 'approved'];
/** The client may cancel a request (and get the amount back) only while nobody has started paying it. */
export const CANCELLABLE_WITHDRAWAL_STATUSES = ['pending', 'under_review'];
export const DEPOSIT_METHODS = [
    { id: 'bank_wire', label: 'Bank wire' },
    { id: 'card', label: 'Card' },
    { id: 'agent', label: 'Agent-assisted' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'other', label: 'Other' },
];
/**
 * Coins never offered to clients even when ticked in the NOWPayments dashboard: a meme token, and the
 * BNB Beacon Chain, which has been shut down.
 */
export const HIDDEN_CRYPTO_COINS = ['catston', 'bnbmainnet'];
/** Display name and network of a NOWPayments coin code. */
export function cryptoCoin(code) {
    const c = code.toLowerCase();
    const KNOWN = {
        btc: ['BTC', 'Bitcoin'],
        eth: ['ETH', 'Ethereum'],
        usdterc20: ['USDT', 'Ethereum (ERC20)'],
        usdttrc20: ['USDT', 'Tron (TRC20)'],
        usdtbsc: ['USDT', 'BNB Smart Chain (BEP20)'],
        usdtsol: ['USDT', 'Solana'],
        usdtmatic: ['USDT', 'Polygon'],
        usdc: ['USDC', 'Ethereum (ERC20)'],
        usdcerc20: ['USDC', 'Ethereum (ERC20)'],
        usdcsol: ['USDC', 'Solana'],
        usdcbsc: ['USDC', 'BNB Smart Chain (BEP20)'],
        usdcmatic: ['USDC', 'Polygon'],
        ltc: ['LTC', 'Litecoin'],
        sol: ['SOL', 'Solana'],
        trx: ['TRX', 'Tron'],
        bnbbsc: ['BNB', 'BNB Smart Chain'],
        xrp: ['XRP', 'XRP Ledger'],
        doge: ['DOGE', 'Dogecoin'],
        ada: ['ADA', 'Cardano'],
        ton: ['TON', 'TON'],
        link: ['LINK', 'Ethereum (ERC20)'],
        avaxc: ['AVAX', 'Avalanche C-Chain'],
        bch: ['BCH', 'Bitcoin Cash'],
        dot: ['DOT', 'Polkadot'],
        xmr: ['XMR', 'Monero'],
        arb: ['ARB', 'Arbitrum One'],
        eurt: ['EURT', 'Ethereum (ERC20)'],
    };
    const k = KNOWN[c];
    return k ? { symbol: k[0], network: k[1] } : { symbol: c.toUpperCase(), network: '' };
}
/**
 * Whether the client sees anything about source of funds: only once their deposits reach the
 * threshold ($10,000), staff asked for it, or they already sent one.
 */
export function sofVisible(c) {
    return c.sofRequired || c.sofStatus !== 'not_required' || Number(c.lifetimeDeposits) >= Number(c.threshold);
}
export const TICKET_CATEGORIES = [
    { id: 'funding', label: 'Deposit/withdrawal related questions' },
    { id: 'verification', label: 'Account Verification questions' },
    { id: 'trading', label: 'Trading related questions' },
    { id: 'technical', label: 'Technical issues' },
    { id: 'callback', label: 'Call back request' },
];
export const ticketCategoryLabel = (c) => (c === 'yield' ? 'Yield early unlock request' : (TICKET_CATEGORIES.find((x) => x.id === c)?.label ?? c));
export const SOF_SOURCES = [
    { id: 'salary', label: 'Salary / employment' },
    { id: 'business', label: 'Business income' },
    { id: 'savings', label: 'Savings' },
    { id: 'investments', label: 'Investments' },
    { id: 'property_sale', label: 'Sale of property' },
    { id: 'inheritance', label: 'Inheritance or gift' },
    { id: 'other', label: 'Other' },
];
export const SOF_INCOME_BANDS = [
    { id: 'under_25k', label: 'Under $25,000' },
    { id: '25k_50k', label: '$25,000 – $50,000' },
    { id: '50k_100k', label: '$50,000 – $100,000' },
    { id: '100k_250k', label: '$100,000 – $250,000' },
    { id: 'over_250k', label: 'Over $250,000' },
];
export const SOF_EXPECTED_BANDS = [
    { id: 'under_10k', label: 'Under $10,000' },
    { id: '10k_25k', label: '$10,000 – $25,000' },
    { id: '25k_50k', label: '$25,000 – $50,000' },
    { id: '50k_100k', label: '$50,000 – $100,000' },
    { id: 'over_100k', label: 'Over $100,000' },
];
export const SOF_DOC_KINDS = [
    { id: 'bank_statement', label: 'Bank statement' },
    { id: 'payslip', label: 'Payslip' },
    { id: 'tax_return', label: 'Tax return' },
    { id: 'other', label: 'Other supporting document' },
];
/** What the reviewer confirms before approving; every item must be ticked. */
export const SOF_CHECKLIST = [
    { id: 'name_matches', label: 'Name on the statements matches the KYC name' },
    { id: 'address_matches', label: 'Address matches the profile, or the difference is explained' },
    { id: 'three_months', label: '3 consecutive months, the latest ending within 45 days' },
    { id: 'unedited', label: 'No signs of editing; balances carry over month to month' },
    { id: 'income_supports', label: 'Income or balances support the deposits and the declared amount' },
    { id: 'source_matches', label: 'Money in matches the declared source' },
];
export const SOF_STATEMENT_MONTHS = 3;
export const SOF_STATEMENT_MAX_AGE_DAYS = 45;
const ym = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;
/** The last `n` full calendar months before `today`, oldest first (YYYY-MM). */
export function lastFullMonths(today, n = SOF_STATEMENT_MONTHS) {
    const out = [];
    for (let i = n; i >= 1; i--) {
        const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
        out.push(ym(d.getUTCFullYear(), d.getUTCMonth()));
    }
    return out;
}
const monthIndex = (s) => Number(s.slice(0, 4)) * 12 + Number(s.slice(5, 7)) - 1;
const lastDay = (s) => new Date(Date.UTC(Number(s.slice(0, 4)), Number(s.slice(5, 7)), 0));
/**
 * Bank statements must cover `months` consecutive calendar months, the latest ending no more than
 * `maxAgeDays` before `today`. `periods` are YYYY-MM ranges. Returns a reason when they don't.
 */
export function statementCoverageError(periods, today, months = SOF_STATEMENT_MONTHS, maxAgeDays = SOF_STATEMENT_MAX_AGE_DAYS) {
    if (!periods.length)
        return `Upload bank statements for the last ${months} months`;
    const covered = new Set();
    for (const p of periods)
        for (let m = monthIndex(p.from); m <= monthIndex(p.to); m++)
            covered.add(m);
    const oldest = new Date(today.getTime() - maxAgeDays * 86400_000);
    // Newest covered month whose statement ends recently enough, then count back.
    const recent = [...covered].filter((m) => lastDay(ym(Math.floor(m / 12), m % 12)) >= oldest && lastDay(ym(Math.floor(m / 12), m % 12)) <= today);
    if (!recent.length)
        return `The latest bank statement must end within the last ${maxAgeDays} days`;
    const end = Math.max(...recent);
    for (let m = end - months + 1; m <= end; m++) {
        if (!covered.has(m))
            return `Bank statements must cover ${months} months in a row; ${ym(Math.floor(m / 12), m % 12)} is missing`;
    }
    return null;
}
//# sourceMappingURL=account.js.map